(() => {
  'use strict';

  if (customElements.get('spicat-cart-recommendations')) return;

  class SpicatCartRecommendations extends HTMLElement {
    static select(cartLines, candidates, random = Math.random) {
      if (!cartLines.length) return [];
      const normalize = (color) => String(color || '').toLowerCase().replace(/^gray$/, 'grey');
      const amounts = new Map();
      cartLines.forEach((line) => {
        const color = normalize(line.color);
        if (color) amounts.set(color, (amounts.get(color) || 0) + Math.max(0, Number(line.amount) || 0));
      });

      const seen = new Set();
      const groups = new Map();
      candidates.forEach((candidate) => {
        const id = String(candidate.variantId);
        const color = normalize(candidate.color);
        if (!id || seen.has(id) || !color) return;
        if (amounts.size && !amounts.has(color)) return;
        seen.add(id);
        if (!groups.has(color)) groups.set(color, []);
        groups.get(color).push(candidate);
      });

      const rankedColors = [...groups.keys()].sort((a, b) => (amounts.get(b) || 0) - (amounts.get(a) || 0));
      const dominant = rankedColors[0];
      const shuffle = (values) => {
        for (let i = values.length - 1; i > 0; i -= 1) {
          const j = Math.floor(random() * (i + 1));
          [values[i], values[j]] = [values[j], values[i]];
        }
        return values;
      };

      const selected = [];
      for (const power of [true, false]) {
        const queues = new Map(rankedColors.map((color) => [color, groups.get(color).filter((item) => item.power === power)]));
        while (selected.length < 6) {
          const colors = shuffle([...queues.keys()].filter((color) => queues.get(color).length));
          if (!colors.length) break;
          if (!selected.length && colors.includes(dominant)) {
            colors.splice(colors.indexOf(dominant), 1);
            colors.unshift(dominant);
          }
          for (const color of colors) {
            selected.push(queues.get(color).shift());
            if (selected.length === 6) break;
          }
        }
        if (selected.length === 6) break;
      }
      return selected;
    }

    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.activated = false;
      const drawer = this.closest('cart-drawer');
      if (!drawer) return;
      if (drawer.classList.contains('active')) this.activate();
      else {
        this.openObserver = new MutationObserver(() => {
          if (drawer.classList.contains('active')) this.activate();
        });
        this.openObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.openObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.imageObserver?.disconnect();
      cancelAnimationFrame(this.navigationFrame);
      clearTimeout(this.reboundTimer);
      if (this.drag && this.track?.hasPointerCapture(this.drag.id)) this.track.releasePointerCapture(this.drag.id);
      this.drag = null;
      this.track?.style.removeProperty('transform');
      this.track?.style.removeProperty('transition');
      this.track?.classList.remove('is-spicat-dragging');
    }

    activate() {
      if (this.activated) return;
      this.activated = true;
      this.openObserver?.disconnect();
      const template = this.querySelector('[data-recommendation-candidates]');
      this.track = this.querySelector('[data-recommendation-track]');
      const cartData = this.querySelector('[data-recommendation-cart]');
      if (!template || !this.track || !cartData) return;
      let cartLines;
      try {
        cartLines = JSON.parse(cartData.textContent);
      } catch {
        return;
      }
      const candidates = Array.from(template.content.querySelectorAll('[data-recommendation-variant]')).map((node) => ({
        variantId: node.dataset.recommendationVariant,
        color: node.dataset.recommendationColor,
        power: node.dataset.recommendationPower === 'true',
        node
      }));
      const selected = SpicatCartRecommendations.select(cartLines, candidates);
      if (!selected.length) return;
      const fragment = document.createDocumentFragment();
      selected.forEach((item) => fragment.append(item.node.cloneNode(true)));
      this.track.replaceChildren(fragment);
      this.hidden = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.previous = this.querySelector('[data-recommendation-direction="-1"]');
      this.next = this.querySelector('[data-recommendation-direction="1"]');
      const { signal } = this.controller;

      this.querySelectorAll('[data-recommendation-direction]').forEach((button) => {
        button.addEventListener('click', () => this.scrollByCard(Number(button.dataset.recommendationDirection)), { signal });
      });
      this.track.addEventListener('scroll', () => this.scheduleNavigation(), { passive: true, signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.scrollByCard(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button')) return;
        if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
        clearTimeout(this.reboundTimer);
        this.track.style.transition = 'none';
        this.track.style.transform = 'translate3d(0, 0, 0)';
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, scroll: this.track.scrollLeft, moved: false, overscroll: 0 };
      }, { passive: true, signal });
      this.track.addEventListener('pointermove', (event) => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const deltaX = event.clientX - this.drag.x;
        const deltaY = event.clientY - this.drag.y;
        if (!this.drag.moved && (Math.abs(deltaX) < 6 || Math.abs(deltaX) <= Math.abs(deltaY))) return;
        event.preventDefault();
        this.drag.moved = true;
        if (!this.track.hasPointerCapture(event.pointerId)) this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-spicat-dragging');
        const theoretical = this.drag.scroll - deltaX;
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        this.track.scrollLeft = Math.min(maximum, Math.max(0, theoretical));
        const excess = theoretical < 0 ? -theoretical : theoretical > maximum ? maximum - theoretical : 0;
        this.drag.overscroll = this.reducedMotion.matches ? 0 : Math.sign(excess) * Math.min(24, Math.abs(excess) * 0.35);
        this.track.style.transform = `translate3d(${Math.round(this.drag.overscroll)}px, 0, 0)`;
      }, { passive: false, signal });
      const endDrag = (event) => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const moved = this.drag.moved;
        const overscroll = this.drag.overscroll;
        this.drag = null;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-spicat-dragging');
        if (moved) this.suppressClickUntil = performance.now() + 350;
        if (overscroll) {
          this.track.style.transition = this.reducedMotion.matches ? 'none' : 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)';
          this.track.style.transform = 'translate3d(0, 0, 0)';
          this.reboundTimer = setTimeout(() => {
            this.track.style.removeProperty('transition');
            this.track.style.removeProperty('transform');
          }, this.reducedMotion.matches ? 0 : 440);
        } else {
          this.track.style.removeProperty('transition');
          this.track.style.removeProperty('transform');
        }
        this.scheduleNavigation();
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => {
        this.track.addEventListener(name, endDrag, { signal });
      });
      this.track.addEventListener('click', (event) => {
        if (performance.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal });
      this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-spicat-cart-add]');
        if (button && this.querySelector('[data-spicat-cart-add][aria-busy="true"]')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { signal });
      document.addEventListener('spicat:cart-add:error', (event) => {
        if (this.contains(event.detail?.button)) this.querySelector('[data-recommendation-status]').textContent = event.detail.error?.message || '';
      }, { signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleNavigation());
        this.resizeObserver.observe(this.track);
      }
      this.loadVisibleImages();
      this.scheduleNavigation();
    }

    scrollByCard(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: (card.getBoundingClientRect().width + gap) * direction, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    scheduleNavigation() {
      if (this.navigationFrame) return;
      this.navigationFrame = requestAnimationFrame(() => {
        this.navigationFrame = 0;
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const position = Math.min(maximum, Math.max(0, this.track.scrollLeft));
        const mediaHeight = this.track.querySelector('.spicat_cart_recommendations_media')?.getBoundingClientRect().height || 80;
        [[this.previous, position <= 2], [this.next, maximum <= 2 || position >= maximum - 2]].forEach(([button, disabled]) => {
          button.style.top = `${Math.max(0, (mediaHeight - button.offsetHeight) / 2)}px`;
          button.disabled = disabled;
          button.tabIndex = disabled ? -1 : 0;
          button.setAttribute('aria-hidden', String(disabled));
        });
      });
    }

    loadVisibleImages() {
      const images = this.track.querySelectorAll('img[data-src]');
      const load = (image) => {
        image.src = image.dataset.src;
        delete image.dataset.src;
      };
      if (typeof IntersectionObserver === 'undefined') {
        images.forEach(load);
        return;
      }
      this.imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          load(entry.target);
          this.imageObserver.unobserve(entry.target);
        });
      }, { root: this.track, rootMargin: '0px 160px' });
      images.forEach((image) => this.imageObserver.observe(image));
    }
  }

  customElements.define('spicat-cart-recommendations', SpicatCartRecommendations);
})();
